import _ from 'lodash';
import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { Button, Modal, FormCheck } from 'react-bootstrap';
import toastr from '../../../toastr';
import Model from '../../model/ShopKeeper.model';

interface IProps {
  autoInquire: boolean;
  container: Object;
  defaultValue: string;
  hide: Function;
  onFleetcrossSelect: Function;
  show: boolean;
  model: Model;
}

interface IFCResult {
  crosses: string;
  DSIpartnumber: string;
  mfrdesc: string;
  mfrindex: string;
  partnumber: string;
  partdesc: string;
  PIM9Match: boolean;
  PIV1Match: boolean;
}

interface ICrossReferences {
  [lineNum: string]: {
    data: IFCResult[];
    show: boolean;
  };
}

const FleetcrossModal: FunctionComponent<IProps> = ({
  autoInquire,
  container,
  defaultValue,
  hide,
  onFleetcrossSelect,
  show,
  model,
}) => {
  const [crossReferencesMap, setCrossReferencesMap] = useState({} as ICrossReferences);
  const [lastError, setLastError] = useState('');
  const [lookupInProcess, setLookupInProcess] = useState(false);
  const [partSearchResult, setPartSearchResult] = useState<IFCResult[] | null>(null);
  const [showFullPartDescriptions, setShowFullPartDescriptions] = useState(false);
  const partInputRef = useRef<HTMLInputElement>(null);

  // Run this effect on initial mount only
  useEffect(() => {
    // timeout required for react-bootstrap portal issue
    setTimeout(() => {
      partInputRef.current!.focus();
      if (autoInquire) {
        lookup();
      }
    }, 0);
  }, []);

  const crossReferenceLookup = (index: number, partnumber: string, mfrindex: string) => {
    // Does this exist in the cross reference data map already?
    if (index in crossReferencesMap) {
      // Flip the visibility but keep it cached
      setCrossReferencesMap({
        ...crossReferencesMap,
        [index]: {
          ...crossReferencesMap[index],
          show: !crossReferencesMap[index].show,
        },
      });
    } else {
      // Otherwise do a fresh API crossreference lookup
      setLookupInProcess(true);
      model.fleetcross(partnumber, mfrindex).then((res: { data: IFCResult[]; error: string | null }) => {
        setLookupInProcess(false);
        if (res.error) {
          console.error(res.error);
        } else {
          if (res.data.length > 0) {
            setCrossReferencesMap({
              ...crossReferencesMap,
              [index]: { data: res.data, show: true },
            });
          } else {
            toastr.error('Error', 'No cross refs found on inquiry.');
            console.warn('No crossreference results.');
          }
        }
      });
    }
  };

  const lookup = () => {
    const partnumber = partInputRef.current!.value.trim();
    if (!!partnumber) {
      if (lookupInProcess) {
        toastr.error('Error', 'Fleetcross Inquiry in Process.  Please wait.');
        return;
      }

      // These will be batched in a sync action
      setLookupInProcess(true);
      setLastError('');
      setPartSearchResult(null);
      setCrossReferencesMap({});

      model.fleetcross(partnumber).then((res: { data: IFCResult[]; error: string | null }) => {
        setLookupInProcess(false);
        if (res.error) {
          setLastError(res.error);
        } else {
          if (res.data.length > 0) {
            setPartSearchResult(res.data);
          } else {
            setLastError('NO RESULTS');
          }
        }
      });
    }
  };

  const select = (result: IFCResult) => {
    if (result.PIV1Match) {
      onFleetcrossSelect(result.DSIpartnumber, 'INVENTORY');
    } else if (result.PIM9Match) {
      onFleetcrossSelect(result.DSIpartnumber, 'MASTER');
    } else {
      onFleetcrossSelect(result.DSIpartnumber, 'NONSTOCKING');
    }
  };

  return (
    <Modal backdrop="static" container={container as any} onHide={hide as any} show={show} size="lg">
      <Modal.Header closeButton={true}>
        <Modal.Title>Fleetcross Inquiry</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="sk-fleetcrossModal">
          <div className="sk-fleetcrossLookupControls">
            <input
              defaultValue={defaultValue}
              type="text"
              id="sk-fleetcrossLookupInput"
              onKeyPress={evt => {
                if (evt.key === 'Enter') {
                  lookup();
                }
              }}
              ref={partInputRef}
            />
            <Button onClick={lookup} className="sk-lookupButton" variant="secondary">
              Lookup
            </Button>
            <FormCheck inline={true}>
              <FormCheck.Input
                type="checkbox"
                id="fc-show-desc"
                className="sk-useMaterialInput ml-1"
                onChange={(checkbox: any) => setShowFullPartDescriptions(checkbox.target.checked)}
              />
              <FormCheck.Label htmlFor="fc-show-desc">Show Full Part Descriptions</FormCheck.Label>
            </FormCheck>
          </div>
          <hr />
          {lastError && <div className="sk-fleetcrossError">{lastError}</div>}
          {partSearchResult && (
            <table className="table table-striped table-sm sk-fleetcrossSearchResults">
              <thead>
                <tr>
                  <th>Part No.</th>
                  <th>Manufacturer</th>
                  <th>Crossrefs</th>
                  <th>Master</th>
                  <th>Inventory</th>
                </tr>
              </thead>
              <tbody>
                {partSearchResult!.map((result: IFCResult, index: number) => (
                  <React.Fragment key={`sk-fc-${index}`}>
                    <tr title={result.partdesc} onClick={() => select(result)}>
                      <td>{result.partnumber}</td>
                      <td>
                        {result.mfrdesc}
                        {showFullPartDescriptions && <div className="sk-partDescription">{result.partdesc}</div>}
                      </td>
                      {_.get(result, 'crosses', 'N') === 'Y' ? (
                        <td
                          className="sk-crossReferenceLink"
                          title="Show Cross References"
                          onClick={e => {
                            crossReferenceLookup(index, result.partnumber, result.mfrindex);
                            e.stopPropagation();
                          }}
                        >
                          Yes
                        </td>
                      ) : (
                        <td />
                      )}
                      <td>{result.PIM9Match && <i className="zmdi zmdi-check" />}</td>
                      <td>{result.PIV1Match && <i className="zmdi zmdi-check" />}</td>
                    </tr>

                    {index in crossReferencesMap &&
                      crossReferencesMap[index].show &&
                      crossReferencesMap[index].data.map((crResult, crIndex) => (
                        <tr
                          key={`sk-fc-${index}-cr-${crIndex}`}
                          className="sk-crossReferenceResult"
                          title={crResult.partdesc}
                          onClick={() => select(crResult)}
                        >
                          <td>{crResult.partnumber}</td>
                          <td>
                            {crResult.mfrdesc}
                            {showFullPartDescriptions && <div className="sk-partDescription">{crResult.partdesc}</div>}
                          </td>
                          <td />
                          <td>{crResult.PIM9Match && <i className="zmdi zmdi-check" />}</td>
                          <td>{crResult.PIV1Match && <i className="zmdi zmdi-check" />}</td>
                        </tr>
                      ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button autoFocus={true} onClick={() => hide()} variant="secondary">
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default FleetcrossModal;
